import type { OrderMode, RestaurantIdentity } from '@/types';
import { restaurantTz, isInClosureOn, isOpenAt, isSameDayInTz, isWeeklyHoursEmpty, nextOpeningAt } from '@/lib/hours';
import { tomorrowCheckoutSlots } from '@/features/checkout/checkoutSlots';
import { useNow } from '@/lib/useNow';
import { isChannelPaused, isOnlineOrderingPaused } from './pausedChannels';
import { closureRunEnd } from './closureLabel';

export type OrderingReason = 'open' | 'operational-off' | 'no-hours' | 'closed-now' | 'preorder' | 'on-vacation';

export interface EffectiveOrderingState {
  canOrder: boolean;
  reason: OrderingReason;
  nextOpeningAt: Date | null;
  closureLabel?: string;
  closureUntil?: string;   // 'YYYY-MM-DD'
  /** DF-0314 — la pré-commande vise le lendemain, pas la fin de la journée. Les
   *  écrans qui n'annoncent qu'une heure doivent nommer le jour. */
  preorderNextDay?: boolean;
}

/**
 * `dineIn` : le client est à table (QR). SC-1434 — la pause se choisit par
 * canal : à table seul le canal « sur place » compte, ailleurs la carte ne se
 * verrouille que si tous les modes proposés (`offeredModes`) sont en pause — un
 * mode en pause parmi d'autres quitte juste le choix, voir `useAllowedModes`.
 */
export function useEffectiveOrderingState(
  identity: RestaurantIdentity,
  dineIn = false,
  offeredModes?: OrderMode[],
): EffectiveOrderingState {
  const now = useNow(60_000);
  if (dineIn ? isChannelPaused(identity, 'dine_in') : isOnlineOrderingPaused(identity, offeredModes)) {
    return { canOrder: false, reason: 'operational-off', nextOpeningAt: null };
  }
  if (isWeeklyHoursEmpty(identity.hours)) {
    return { canOrder: false, reason: 'no-hours', nextOpeningAt: null };
  }
  const tz = restaurantTz(identity);
  const matched = isInClosureOn(identity.closures, now, tz);
  if (matched) {
    const next = nextOpeningAt(identity.hours, now, tz, identity.closures);
    return {
      canOrder: false,
      reason: 'on-vacation',
      nextOpeningAt: next,
      closureLabel: matched.label,
      closureUntil: closureRunEnd(identity.closures, matched.to),
    };
  }
  if (isOpenAt(identity.hours, now, tz, identity.closures)) {
    return { canOrder: true, reason: 'open', nextOpeningAt: null };
  }
  const next = nextOpeningAt(identity.hours, now, tz, identity.closures);
  if (next && isSameDayInTz(now, next, tz)) {
    return { canOrder: true, reason: 'preorder', nextOpeningAt: next };
  }
  // DF-0314 — entre la fermeture et minuit, la prochaine ouverture tombe le
  // lendemain civil : la carte se verrouillait alors entièrement, le client
  // lisait « ce restaurant n'accepte pas de commandes en ligne », et le site se
  // rallumait tout seul à minuit pour le MÊME service du lendemain midi. Sur
  // 17 des 23 enseignes en ligne, une à trois heures perdues chaque nuit.
  //
  // Le tunnel J+1 existe depuis `b387720` (onglet Demain, créneaux du lendemain,
  // bandeau du panier) : il était seulement inatteignable, parce qu'on n'y entre
  // qu'avec un panier déjà rempli. La garde « même jour » le précède de quatre
  // mois et n'a pas été levée avec lui.
  //
  // Le feu vert se prend sur les créneaux de demain, pas sur le calendrier : si
  // le restaurant est fermé demain aussi, la finalisation n'aurait rien à
  // proposer et la carte doit rester verrouillée.
  if (next && tomorrowCheckoutSlots(identity.hours, now, undefined, identity.closures, tz).length > 0) {
    return { canOrder: true, reason: 'preorder', nextOpeningAt: next, preorderNextDay: true };
  }
  return { canOrder: false, reason: 'closed-now', nextOpeningAt: next };
}
